import React, { useMemo, useState } from 'react';
import { Button, Select, Radio, Spin } from 'antd';
import { ArrowLeftOutlined, ArrowRightOutlined } from '@ant-design/icons';
import useSkuSearch from '../hooks/useSkuSearch';

const StepProducts = ({ onNext, onBack, formData, updateField }) => {
  const [errors, setErrors] = useState({});

  const activeSkuField = formData.listType === 'Excluded' ? 'excludedSkus' : 'includedSkus';
  const selectedSkus = useMemo(
    () => (Array.isArray(formData[activeSkuField]) ? formData[activeSkuField] : []),
    [activeSkuField, formData]
  );

  const {
    options: includedSkuSelectOptions,
    onSearch: searchSkus,
    onPopupScroll: onIncludedSkuPopupScroll,
    loading: skuSearchLoading,
    loadingMore: includedSkuLoadingMore,
    hasMore: includedSkuHasMore,
  } = useSkuSearch({ storeLabel: formData.storefront, selectedSkus });

  const handleNext = () => {
    const nextErrors = {};

    setErrors(nextErrors);

    if (Object.values(nextErrors).some(Boolean)) return;
    onNext();
  };

  const handleFieldChange = (field, value) => {
    updateField(field, value);
    if (errors[field]) {
      setErrors((current) => ({ ...current, [field]: false }));
    }
  };

  const skuListLabel = formData.listType === 'Excluded' ? 'Excluded SKUs' : 'Included SKUs';

  return (
    <div >
      <div className='step-container'>
        <div className='step-content'>
          <div className='step-header'>
            <div className='step-title'>
              Products (Include/Exclude)
            </div>
            <div className='step-subtitle'>
              Choose which products this rule applies to.
            </div>
          </div>

          <div className='step-form'>
            <div className='step-group border-bottom-light'>
              <div className='step-form-label'>Rule Type</div>
              <div className='RadioGroup'>
                <Radio.Group
                  value={formData.listType}
                  onChange={(e) => updateField('listType', e.target.value)}
                >
                  <Radio.Button value='Included'>
                    Included
                  </Radio.Button>
                  <Radio.Button value='Excluded'>
                    Excluded
                  </Radio.Button>
                </Radio.Group>
              </div>
              <ul className='some-text mt-0'>
                <li>
                  Include List: Only selected SKUs will get a discount. Other products will be ignored.
                </li>
                <li>
                  Exclusion List: Selected SKUs will not get a discount. All other products will get a discount.
                </li>
              </ul>
            </div>
            <div className='step-group'>
              <div className='step-form-label'>SKU Source</div>
              <div className='RadioGroup'>
                <Radio.Group
                  value={formData.skuSource}
                  onChange={(e) => updateField('skuSource', e.target.value)}
                >
                  <Radio.Button value='Pre-Defined List' disabled>
                    Pre-Defined List
                  </Radio.Button>
                  <Radio.Button value='Custom SKUs'>
                    Custom SKUs
                  </Radio.Button>
                </Radio.Group>
              </div>
              <p className='some-text mt-16'>
                Use a centrally-managed list or your custom SKUs — not both.
              </p>
            </div>
            <div className='step-group'>
              <div className='step-form-label'>
                {skuListLabel}
              </div>
              <Select
                mode="multiple"
                showSearch
                filterOption={false}
                onSearch={searchSkus}
                onPopupScroll={onIncludedSkuPopupScroll}
                notFoundContent={
                  skuSearchLoading
                    ? <span><Spin size="small" style={{ marginRight: 8 }} />Loading SKUs...</span>
                    : (formData.storefront ? 'No SKUs found' : 'Select a Store first')
                }
                dropdownRender={(menu) => (
                  <>
                    {menu}
                    {includedSkuLoadingMore && (
                      <div style={{ textAlign: 'center', padding: 8, color: 'rgba(0,0,0,0.45)' }}>
                        <Spin size="small" style={{ marginRight: 8 }} />Loading more SKUs...
                      </div>
                    )}
                    {!includedSkuLoadingMore && !includedSkuHasMore && includedSkuSelectOptions.length > 0 && (
                      <div style={{ textAlign: 'center', padding: 8, color: 'rgba(0,0,0,0.45)' }}>
                        No more SKUs to load
                      </div>
                    )}
                  </>
                )}
                placeholder={`Search and select ${skuListLabel}`}
                options={includedSkuSelectOptions}
                value={selectedSkus}
                status={errors[activeSkuField] ? 'error' : undefined}
                onChange={(value) => handleFieldChange(activeSkuField, value)}
                className={`Input-border InputSelect w-100 height-auto ${formData.listType === 'Excluded' ? 'sku-select-excluded' : 'sku-select-included'}`}
                style={{ width: '100%' }}
                disabled={formData.skuSource !== 'Custom SKUs' || !formData.storefront}
              />
              {errors[activeSkuField] && (
                <div className='step-form-hint' style={{ color: '#ff4d4f' }}>At least one {skuListLabel.slice(0, -1)} is required.</div>
              )}
              {!formData.storefront && <div className='step-form-hint'>Select a Store in Basic Info to search SKUs.</div>}
            </div>
          </div>
          <div className='step-footer mt-32'>
            <Button onClick={() => onBack(2)} className='btn-s'>
              <ArrowLeftOutlined />
              Previous: Eligibility Conditions
            </Button>
            <Button type="primary" onClick={handleNext} className='btn-p'>
              Next: Review & Publish
              <ArrowRightOutlined />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StepProducts;
